import { useCallback, useMemo, useState } from "react";
import { useHoldingsData } from "creso-core/hooks";
import { SchemeWiseHolding } from "creso-core/types";
import {
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  CommandList,
  CommandEmpty,
  Command,
  CommandInput,
  CommandItem,
  CommandGroup,
} from "web-design-system";
import { ChevronsUpDown, Check } from "lucide-react";

interface Props {
  onChange?: (holdingScheme: SchemeWiseHolding) => void;
  investmentAccountGuid: string;
  schemefilter?: (holdingScheme: SchemeWiseHolding) => boolean;
  value: SchemeWiseHolding | null;
  className?: string;
}

export function HoldingSchemesDropdown(props: Props) {
  const {
    investmentAccountGuid,
    onChange,
    schemefilter = Boolean,
    value,
    className,
  } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: holdingSchemes = [] } = useHoldingsData(investmentAccountGuid);

  const options = useMemo(() => {
    // Dedupe by ISIN, preferring a row that passes schemefilter
    const byIsin = new Map<string, SchemeWiseHolding>();
    holdingSchemes.forEach((scheme) => {
      const existing = byIsin.get(scheme.scheme_isin);
      if (!existing || (!schemefilter(existing) && schemefilter(scheme))) {
        byIsin.set(scheme.scheme_isin, scheme);
      }
    });
    const uniqueSchemes = Array.from(byIsin.values());
    const filtered = search
      ? uniqueSchemes.filter((d) =>
          d.scheme_name.toLowerCase().includes(search.toLowerCase())
        )
      : uniqueSchemes;

    return filtered.map((d) => ({
      label: d.scheme_name,
      value: d.scheme_isin,
      scheme: d,
      disabled: !schemefilter(d),
    }));
  }, [holdingSchemes, schemefilter, search]);

  const onChangeHandler = useCallback(
    (scheme: SchemeWiseHolding) => {
      onChange?.(scheme);
      setOpen(false);
    },
    [onChange]
  );

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between shadow-none", className)}
        >
          <div
            className={cn(
              "truncate font-normal text-left",
              !value && "text-muted-foreground"
            )}
          >
            {value
              ? options.find((option) => option.value === value.scheme_isin)
                  ?.label || value.scheme_name
              : "Select scheme"}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width)"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={(v) => setSearch(v)}
            placeholder="Search scheme..."
          />
          <CommandList>
            <CommandEmpty>No scheme found</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  onSelect={() => onChangeHandler(option.scheme)}
                >
                  <div className="flex flex-row gap-2 items-center w-full">
                    <span className="flex-1">{option.label}</span>
                    <Check
                      className={cn(
                        "h-4 w-4",
                        value?.scheme_isin === option.value
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
